import React, { useEffect, useRef, useState } from 'react'

const processSteps = [
  {
    number: "01",
    title: "Client Discovery & Goal Understanding",
    icon: "🔍",
    points: [
      "In-depth discussion to understand personal or business aspirations",
      "Identifying current life challenges related to career, finances or relationships",
      "Understanding short-term and long-term goals for alignment",
      "Clarifying expectations and desired outcomes from the consultation",
    ],
  },
  {
    number: "02",
    title: "Collection of Personal Inputs & Concerns",
    icon: "📝",
    points: [
      "Understanding current obstacles, recurring patterns and blockages",
      "Gathering information about significant life events and turning points",
      "Identifying areas of stagnation — personal, professional or financial",
      "Noting any specific concerns around health, wealth or relationships",
    ],
  },
  {
    number: "03",
    title: "Basic Details Collection",
    icon: "📋",
    points: [
      "Full name as per birth certificate and commonly used name",
      "Date of Birth for life path and destiny number calculation",
      "Business or company name (if applicable) for entity analysis",
      "Any other names, signatures or numbers requiring evaluation",
    ],
  },
  {
    number: "04",
    title: "Numerological Analysis of Name & Numbers",
    icon: "🔢",
    points: [
      "Calculation of Life Path, Destiny, Soul Urge and Personality numbers",
      "Analysis of name vibrations and their alignment with birth numbers",
      "Identification of karmic numbers and master numbers in the chart",
      "Mapping number patterns to life events, decisions and outcomes",
    ],
  },
  {
    number: "05",
    title: "Identification of Strengths, Imbalances & Opportunities",
    icon: "⚡",
    points: [
      "Highlighting numerological strengths and natural talents",
      "Detecting imbalances in current name or number vibrations",
      "Revealing hidden opportunities aligned with personal number cycles",
      "Understanding the impact of missing numbers on life experience",
    ],
  },
]

const suggestions = [
  { icon: "✍️", title: "Name Correction", desc: "Align your personal name vibration with your birth numbers for enhanced harmony, success and energy flow in daily life." },
  { icon: "🏢", title: "Company / Business Name", desc: "Choose a business name that resonates with your goals and attracts prosperity, growth and the right opportunities." },
  { icon: "™️", title: "Brand & Logo Name", desc: "Craft a brand identity backed by powerful numerical vibrations that create a strong, memorable and magnetic market presence." },
  { icon: "🖊️", title: "Signature Correction", desc: "Your signature is your energetic stamp — align it with your numbers to project confidence, authority and positive intent." },
  { icon: "🏦", title: "Bank & Account Guidance", desc: "Select bank names, account numbers or financial identifiers that support wealth accumulation and financial stability." },
  { icon: "🏡", title: "House / Property Name", desc: "Name your home or property with vibrations that promote peace, prosperity and well-being for all residents." },
  { icon: "🔢", title: "Important Number Alignments", desc: "Guidance on lucky numbers, favorable dates, vehicle numbers and other significant digits that influence your journey." },
]

const ProcessCard = ({ step, index }) => {
  const [visible, setVisible] = useState(false)
  const ref = useRef()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true) },
      { threshold: 0.15 }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${index * 0.1}s, transform 0.7s ease ${index * 0.1}s`,
        background: 'linear-gradient(135deg, rgba(130,94,10,0.08) 0%, rgba(255,255,255,0.95) 100%)',
        border: '1px solid rgba(130,94,10,0.18)',
        borderLeft: '4px solid #825e0a',
        borderRadius: '12px',
        padding: '32px 28px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        boxShadow: '0 4px 24px rgba(130,94,10,0.07)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 12px 40px rgba(130,94,10,0.18)'
        e.currentTarget.style.borderLeftColor = '#c48a14'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 4px 24px rgba(130,94,10,0.07)'
        e.currentTarget.style.borderLeftColor = '#825e0a'
      }}
    >
      <div style={{
        position: 'absolute', top: '-10px', right: '16px',
        fontSize: '90px', fontFamily: '"Playfair Display", serif',
        color: 'rgba(130,94,10,0.06)', fontWeight: '900',
        lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
      }}>
        {step.number}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #825e0a, #c48a14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(130,94,10,0.3)',
        }}>
          {step.icon}
        </div>
        <div>
          <span style={{
            fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
          }}>
            Step {step.number}
          </span>
          <h3 style={{
            margin: '4px 0 0', fontSize: '18px', fontWeight: '700',
            color: '#2c1a00', fontFamily: '"Playfair Display", serif',
            lineHeight: '1.3',
          }}>
            {step.title}
          </h3>
        </div>
      </div>

      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {step.points.map((point, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{
              width: '18px', height: '18px', borderRadius: '50%',
              background: 'rgba(130,94,10,0.1)',
              border: '1.5px solid rgba(130,94,10,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: '2px',
            }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#825e0a', display: 'block' }} />
            </span>
            <span style={{ fontSize: '14px', color: '#5a3d10', lineHeight: '1.6', fontFamily: '"Lato", sans-serif' }}>
              {point}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const SuggestionCard = ({ item, index }) => {
  const [visible, setVisible] = useState(false)
  const ref = useRef()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true) },
      { threshold: 0.1 }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.6s ease ${index * 0.08}s, transform 0.6s ease ${index * 0.08}s`,
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(196,138,20,0.2)',
        borderRadius: '14px',
        padding: '28px 22px',
        textAlign: 'center',
        cursor: 'default',
        transition: `opacity 0.6s ease ${index * 0.08}s, transform 0.6s ease ${index * 0.08}s, background 0.3s ease`,
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.11)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
    >
      <div style={{
        width: '56px', height: '56px', borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(130,94,10,0.5), rgba(196,138,20,0.5))',
        border: '1px solid rgba(196,138,20,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '24px', margin: '0 auto 16px',
        boxShadow: '0 4px 16px rgba(130,94,10,0.3)',
      }}>
        {item.icon}
      </div>
      <h4 style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '17px', fontWeight: '700',
        color: '#f0c060', marginBottom: '10px',
      }}>
        {item.title}
      </h4>
      <p style={{
        fontSize: '13px', color: 'rgba(253,248,240,0.65)',
        fontFamily: '"Lato", sans-serif', lineHeight: '1.7',
      }}>
        {item.desc}
      </p>
    </div>
  )
}

const Numerology = () => {
  const [headerVisible, setHeaderVisible] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setHeaderVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Montserrat:wght@400;600;700&family=Lato:wght@300;400;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #fdf8f0; }

        .num-section { min-height: 100vh; background: #fdf8f0; font-family: 'Lato', sans-serif; overflow: hidden; }

        .num-hero {
          position: relative;
          background: linear-gradient(160deg, #2c1a00 0%, #4a2e05 40%, #825e0a 100%);
          padding: 80px 24px 100px;
          text-align: center;
          overflow: hidden;
        }

        .mandala-ring {
          position: absolute; border-radius: 50%;
          border: 1px solid rgba(196,138,20,0.12);
          top: 50%; left: 50%; pointer-events: none;
        }
        .spin { animation: rotateSlow 60s linear infinite; }
        .spin-rev { animation: rotateSlowRev 80s linear infinite; }
        @keyframes rotateSlow { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(360deg); } }
        @keyframes rotateSlowRev { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(-360deg); } }

        .badge {
          display: inline-block;
          background: rgba(196,138,20,0.15);
          border: 1px solid rgba(196,138,20,0.4);
          color: #f0c060; padding: 6px 18px; border-radius: 40px;
          font-size: 11px; letter-spacing: 3px; text-transform: uppercase;
          font-family: 'Montserrat', sans-serif; font-weight: 700; margin-bottom: 20px;
        }

        .sub-badges { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; margin-top: 24px; }
        .sub-badge {
          background: rgba(253,248,240,0.08); border: 1px solid rgba(240,192,96,0.25);
          color: rgba(253,248,240,0.7); padding: 6px 16px; border-radius: 30px;
          font-size: 12px; font-family: 'Montserrat', sans-serif; font-weight: 600; letter-spacing: 0.5px;
        }

        .divider-ornament { display: flex; align-items: center; justify-content: center; gap: 12px; margin: 16px auto 0; }
        .line-gold { height: 1px; width: 60px; background: linear-gradient(to right, transparent, #c48a1488, #c48a14); }
        .line-gold-rev { height: 1px; width: 60px; background: linear-gradient(to left, transparent, #c48a1488, #c48a14); }

        .floating-numbers {
          position: absolute; inset: 0; pointer-events: none; overflow: hidden;
        }
        .float-num {
          position: absolute;
          font-family: '"Playfair Display", serif';
          color: rgba(196,138,20,0.07);
          font-size: 80px; font-weight: 900; user-select: none;
          animation: floatUp 12s ease-in-out infinite;
        }
        @keyframes floatUp {
          0%, 100% { transform: translateY(0); opacity: 0.07; }
          50% { transform: translateY(-20px); opacity: 0.12; }
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px; max-width: 1100px; margin: 0 auto; padding: 48px 24px 64px;
        }

        .suggestions-strip {
          background: linear-gradient(160deg, #2c1a00 0%, #4a2e05 60%, #3a2204 100%);
          padding: 72px 24px;
        }
        .suggestions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px; max-width: 1100px; margin: 40px auto 0;
        }

        .section-heading { text-align: center; padding: 64px 24px 0; }

        .cta-strip {
          background: linear-gradient(135deg, #825e0a, #4a2e05);
          padding: 56px 24px; text-align: center; position: relative; overflow: hidden;
        }
        .cta-strip::before { content: '✦'; position: absolute; top: 24px; left: 40px; color: rgba(255,255,255,0.06); font-size: 60px; }
        .cta-strip::after  { content: '✦'; position: absolute; bottom: 24px; right: 40px; color: rgba(255,255,255,0.06); font-size: 60px; }

        .cta-btn {
          display: inline-block;
          background: linear-gradient(135deg, #f0c060, #c48a14);
          color: #2c1a00; padding: 16px 44px; border-radius: 50px;
          font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 15px;
          letter-spacing: 1px; border: none; cursor: pointer;
          box-shadow: 0 8px 28px rgba(196,138,20,0.4);
          transition: transform 0.25s ease, box-shadow 0.25s ease; text-decoration: none;
        }
        .cta-btn:hover { transform: translateY(-3px); box-shadow: 0 14px 36px rgba(196,138,20,0.55); }
      `}</style>

      <section className="num-section">

        {/* Hero */}
        <div className="num-hero">
          {[500, 700, 900].map((s, i) => (
            <div key={i} className={`mandala-ring ${i % 2 === 0 ? 'spin' : 'spin-rev'}`} style={{ width: s, height: s }} />
          ))}

          {/* Floating background numbers */}
          <div className="floating-numbers">
            {['1','3','7','9','11','22'].map((n, i) => (
              <span key={i} className="float-num" style={{
                left: `${10 + i * 15}%`, top: `${15 + (i % 3) * 25}%`,
                animationDelay: `${i * 2}s`, fontSize: `${60 + i * 10}px`,
              }}>{n}</span>
            ))}
          </div>

          <div style={{
            position: 'relative', zIndex: 2,
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(-30px)',
            transition: 'opacity 0.9s ease, transform 0.9s ease',
          }}>
            <div className="badge">✦ Sacred Science of Numbers ✦</div>

            <h1 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(30px, 5vw, 52px)',
              fontWeight: '900', color: '#fdf8f0',
              lineHeight: '1.2', marginBottom: '16px',
              textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            }}>
              Numerology
              <br />
              <span style={{ color: '#f0c060' }}>Consultation Process</span>
            </h1>

            <div className="divider-ornament">
              <div className="line-gold-rev" />
              <span style={{ color: '#c48a14', fontSize: '18px' }}>✦</span>
              <div className="line-gold" />
            </div>

            <p style={{
              marginTop: '20px', color: 'rgba(253,248,240,0.75)',
              fontSize: '17px', fontFamily: '"Lato", sans-serif',
              fontWeight: '300', letterSpacing: '0.5px',
              maxWidth: '540px', margin: '20px auto 0', lineHeight: '1.8',
            }}>
              Aligning Names & Numbers for Success
            </p>

            <div className="sub-badges">
              <span className="sub-badge">🔢 Name Analysis</span>
              <span className="sub-badge">📅 Birth Numbers</span>
              <span className="sub-badge">🏢 Business Naming</span>
              <span className="sub-badge">✍️ Signature Correction</span>
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '60px' }}>
              <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" fill="#fdf8f0" />
            </svg>
          </div>
        </div>

        {/* Intro */}
        <div style={{ maxWidth: '820px', margin: '0 auto', padding: '64px 24px 0', textAlign: 'center' }}>
          <span style={{
            fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            display: 'block', marginBottom: '16px',
          }}>
            What is Numerology?
          </span>
          <p style={{
            fontSize: '16px', color: '#5a3d10', lineHeight: '1.9',
            fontFamily: '"Lato", sans-serif', fontWeight: '300',
          }}>
            Numerology is the ancient study of the mystical relationship between <strong style={{ color: '#825e0a', fontWeight: '700' }}>numbers and life events</strong>. Every name, date and number carries a unique vibration that influences your personality, destiny and experiences. By understanding and aligning these vibrations — through your <strong style={{ color: '#825e0a' }}>Life Path, Destiny, Soul Urge</strong> and other core numbers — you can make empowered decisions in business, relationships and everyday life to attract abundance and harmony.
          </p>
        </div>

        {/* Process Steps */}
        <div className="section-heading">
          <span style={{
            fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
          }}>
            Our Process
          </span>
          <h2 style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(24px, 3vw, 36px)',
            color: '#2c1a00', fontWeight: '700', marginTop: '8px',
          }}>
            How We Decode Your Numbers
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '12px' }}>
            <div className="line-gold-rev" />
            <span style={{ color: '#c48a14', fontSize: '16px' }}>✦</span>
            <div className="line-gold" />
          </div>
        </div>

        <div className="steps-grid">
          {processSteps.map((step, i) => (
            <ProcessCard key={step.number} step={step} index={i} />
          ))}
        </div>

        {/* Suggestions Strip */}
        <div className="suggestions-strip">
          <div style={{ textAlign: 'center' }}>
            <span style={{
              fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
              color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            }}>
              Personalised Guidance
            </span>
            <h2 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(24px, 3vw, 36px)',
              color: '#fdf8f0', fontWeight: '700', marginTop: '8px',
            }}>
              Suggestions & Corrections We Offer
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '12px' }}>
              <div style={{ height: '1px', width: '60px', background: 'linear-gradient(to right, transparent, #c48a1488, #c48a14)' }} />
              <span style={{ color: '#c48a14', fontSize: '16px' }}>✦</span>
              <div style={{ height: '1px', width: '60px', background: 'linear-gradient(to left, transparent, #c48a1488, #c48a14)' }} />
            </div>
            <p style={{
              marginTop: '16px', color: 'rgba(253,248,240,0.6)',
              fontSize: '15px', fontFamily: '"Lato", sans-serif',
              maxWidth: '560px', margin: '16px auto 0', lineHeight: '1.8',
            }}>
              From personal names to business identities — we align every aspect of your world with the right vibrations.
            </p>
          </div>

          <div className="suggestions-grid">
            {suggestions.map((item, i) => (
              <SuggestionCard key={i} item={item} index={i} />
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="cta-strip">
          <p style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(22px, 3vw, 32px)',
            color: '#fdf8f0', marginBottom: '8px', fontWeight: '700',
          }}>
            Unlock the Power of Your Numbers
          </p>
          <p style={{
            color: 'rgba(253,248,240,0.65)', fontSize: '15px',
            fontFamily: '"Lato", sans-serif', marginBottom: '32px', letterSpacing: '0.5px',
          }}>
            Book a personalised Numerology consultation and align your life with success
          </p>
          <a href="https://wa.me/919122397324?text=Hello%20Tarun%2C%20I%E2%80%99m%20interested%20in%20Numerology%20Consultation." className="cta-btn">
            Book a Consultation ✦
          </a>
        </div>

      </section>
    </>
  )
}

export default Numerology